import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Target, CheckCircle, Clock, AlertTriangle, Loader2,
  RefreshCw, ChevronDown, ChevronUp, User, Flag,
  BarChart3, Layers, Settings, ArrowRight, Workflow, Shield,
  HelpCircle, CircleDot, Check,
} from 'lucide-react';
import { projectId } from '../utils/supabase/info';
import { getAuthHeaders, getAuthHeadersWithJson } from '../utils/apiHeaders';
import { toast } from 'sonner';

// ─── Types ───

interface EngagementWorkspaceProps {
  companyId: string;
  accessToken: string;
  currentUser: { id: string; name?: string; email?: string; role?: string };
}

interface Objective {
  id: string;
  description: string;
  type: string;
  priority: string;
  owner: string;
}

type DeliveryStatus = 'needs_analysis' | 'defined' | 'in_progress' | 'done';

interface DeliveryItem {
  id: string;
  title: string;
  description?: string;
  category: string;
  status: DeliveryStatus;
  owner: string | null;
  ownerName: string | null;
  source: string;
  sourceType: string;
  metricType?: string;
  createdAt: string;
  updatedAt?: string;
}

interface FollowUp {
  id: string;
  title: string;
  action?: string;
  source: string;
  assignedTo: string | null;
  assignedToName: string | null;
  status: 'open' | 'resolved';
  createdAt: string;
  updatedAt?: string;
}

interface Workspace {
  initialized: boolean;
  initializedAt: string;
  initializedByName: string;
  objectives: Objective[];
  deliveryItems: DeliveryItem[];
  followUps: FollowUp[];
  updatedAt: string;
}

// ─── Constants ───

const statusConfig: Record<DeliveryStatus, { label: string; color: string; icon: React.ReactNode }> = {
  needs_analysis: { label: 'Needs analysis', color: 'bg-gray-100 text-gray-700 border-gray-300', icon: <CircleDot className="w-3 h-3" /> },
  defined: { label: 'Defined', color: 'bg-blue-50 text-blue-700 border-blue-300', icon: <Check className="w-3 h-3" /> },
  in_progress: { label: 'In progress', color: 'bg-amber-50 text-amber-700 border-amber-300', icon: <Clock className="w-3 h-3" /> },
  done: { label: 'Done', color: 'bg-emerald-50 text-emerald-700 border-emerald-300', icon: <CheckCircle className="w-3 h-3" /> },
};

const categoryIcon: Record<string, React.ReactNode> = {
  Reporting: <BarChart3 className="w-3.5 h-3.5" />,
  Migration: <Layers className="w-3.5 h-3.5" />,
  Workflow: <Workflow className="w-3.5 h-3.5" />,
  Configuration: <Settings className="w-3.5 h-3.5" />,
  Compliance: <Shield className="w-3.5 h-3.5" />,
  Planning: <Target className="w-3.5 h-3.5" />,
  Integration: <Layers className="w-3.5 h-3.5" />,
  General: <CircleDot className="w-3.5 h-3.5" />,
};

const categoryColor: Record<string, string> = {
  Reporting: 'text-indigo-600',
  Migration: 'text-orange-600',
  Workflow: 'text-purple-600',
  Configuration: 'text-blue-600',
  Compliance: 'text-red-600',
  Planning: 'text-emerald-600',
  Integration: 'text-cyan-600',
  General: 'text-gray-500',
};

const priorityBadge: Record<string, string> = {
  high: 'bg-red-50 text-red-700 border-red-200',
  medium: 'bg-amber-50 text-amber-700 border-amber-200',
  low: 'bg-gray-50 text-gray-600 border-gray-200',
};

// ─── Component ───

export function EngagementWorkspace({ companyId, accessToken, currentUser }: EngagementWorkspaceProps) {
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [loading, setLoading] = useState(true);
  const [initializing, setInitializing] = useState(false);
  const [updatingItem, setUpdatingItem] = useState<string | null>(null);
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    objectives: true, delivery: true, followups: true,
  });

  const fetchWorkspace = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/delivery-workspace/${companyId}`,
        { headers: getAuthHeaders(accessToken) }
      );
      if (!resp.ok) throw new Error('Failed to load workspace');
      const data = await resp.json();
      if (data.initialized && data.workspace) {
        setWorkspace(data.workspace);
      } else {
        setWorkspace(null);
      }
    } catch (err) {
      console.error('[WORKSPACE] Load failed:', err);
      toast.error('Failed to load delivery workspace');
    } finally { setLoading(false); }
  }, [companyId, accessToken]);

  useEffect(() => { fetchWorkspace(); }, [fetchWorkspace]);

  const initializeWorkspace = async () => {
    setInitializing(true);
    try {
      // Pass insight statuses from localStorage so backend knows which are flagged
      let insightStatuses: Record<string, string> = {};
      try {
        const raw = localStorage.getItem(`pk_insight_statuses_${companyId}`);
        if (raw) insightStatuses = JSON.parse(raw);
      } catch {}

      const resp = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/delivery-workspace/${companyId}/initialize`,
        {
          method: 'POST',
          headers: getAuthHeadersWithJson(accessToken),
          body: JSON.stringify({
            userId: currentUser.id,
            userName: currentUser.name || currentUser.email,
            insightStatuses,
          }),
        }
      );
      if (!resp.ok) throw new Error((await resp.json().catch(() => ({}))).error || 'Failed to initialize');
      const data = await resp.json();
      if (data.workspace) {
        setWorkspace(data.workspace);
        toast.success('Delivery workspace initialized');
      }
    } catch (err) {
      console.error('[WORKSPACE] Initialize failed:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to initialize workspace');
    } finally { setInitializing(false); }
  };

  const updateItem = async (itemId: string, updates: Record<string, any>) => {
    setUpdatingItem(itemId);
    try {
      const resp = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/delivery-workspace/${companyId}/items/${itemId}`,
        {
          method: 'PUT',
          headers: getAuthHeadersWithJson(accessToken),
          body: JSON.stringify(updates),
        }
      );
      if (!resp.ok) throw new Error('Failed to update');

      // Optimistic update
      setWorkspace(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          deliveryItems: prev.deliveryItems.map(i => i.id === itemId ? { ...i, ...updates } : i),
          followUps: prev.followUps.map(i => i.id === itemId ? { ...i, ...updates } : i),
        };
      });
    } catch (err) {
      console.error('[WORKSPACE] Update failed:', err);
      toast.error('Failed to update item');
    } finally { setUpdatingItem(null); }
  };

  const toggleSection = (key: string) => setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));

  // ─── Loading ───
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3">
        <Loader2 className="w-7 h-7 animate-spin text-purple-600" />
        <p className="text-sm text-gray-500">Loading delivery workspace...</p>
      </div>
    );
  }

  // ─── Not initialized ───
  if (!workspace) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4 max-w-md mx-auto text-center">
        <div className="w-14 h-14 rounded-full bg-purple-100 flex items-center justify-center">
          <ArrowRight className="w-7 h-7 text-purple-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">
          Ready to set up delivery
        </h3>
        <p className="text-sm text-gray-500 leading-relaxed">
          Convert your engagement insights and project goals into an actionable delivery plan.
          This will create objectives, delivery items, and follow-up tasks based on your finalized engagement context.
        </p>
        <Button
          onClick={initializeWorkspace}
          disabled={initializing}
          className="bg-purple-600 hover:bg-purple-700 mt-2"
        >
          {initializing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
          {initializing ? 'Setting up...' : 'Proceed to Delivery Setup'}
        </Button>
        <p className="text-[11px] text-gray-400 mt-1">
          You can reinitialize later if engagement context changes.
        </p>
      </div>
    );
  }

  // ─── Computed stats ───
  const totalItems = workspace.deliveryItems.length;
  const doneItems = workspace.deliveryItems.filter(i => i.status === 'done').length;
  const inProgressItems = workspace.deliveryItems.filter(i => i.status === 'in_progress').length;
  const openFollowUps = workspace.followUps.filter(f => f.status === 'open').length;

  const categories = [...new Set(workspace.deliveryItems.map(i => i.category))].sort();

  const filteredItems = workspace.deliveryItems.filter(i => {
    if (filterCategory !== 'all' && i.category !== filterCategory) return false;
    if (filterStatus !== 'all' && i.status !== filterStatus) return false;
    return true;
  });

  const SectionHeader = ({ title, icon, iconColor, badge, sectionKey }: {
    title: string; icon: React.ReactNode; iconColor: string; badge?: React.ReactNode; sectionKey: string;
  }) => (
    <div
      className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50/70 transition-colors"
      onClick={() => toggleSection(sectionKey)}
    >
      <div className="flex items-center gap-2">
        <span className={iconColor}>{icon}</span>
        <span className="text-sm font-semibold text-gray-900" style={{ fontSize: '0.875rem', fontWeight: 600 }}>{title}</span>
        {badge}
      </div>
      {expandedSections[sectionKey] ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            Delivery Workspace
          </h2>
          <p className="text-[11px] text-gray-400 mt-1">
            Initialized {new Date(workspace.initializedAt).toLocaleString()} by {workspace.initializedByName}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchWorkspace} className="text-xs">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={initializeWorkspace} disabled={initializing} className="text-xs">
            {initializing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
            Reinitialize
          </Button>
        </div>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-gray-900" style={{ fontWeight: 700 }}>{totalItems}</p>
          <p className="text-[11px] text-gray-500 mt-0.5">Delivery items</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-amber-600" style={{ fontWeight: 700 }}>{inProgressItems}</p>
          <p className="text-[11px] text-gray-500 mt-0.5">In progress</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-emerald-600" style={{ fontWeight: 700 }}>{doneItems}</p>
          <p className="text-[11px] text-gray-500 mt-0.5">Done</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-blue-600" style={{ fontWeight: 700 }}>{openFollowUps}</p>
          <p className="text-[11px] text-gray-500 mt-0.5">Follow-ups open</p>
        </div>
      </div>

      {/* ━━━ Confirmed Objectives ━━━ */}
      <Card className="border border-gray-200 overflow-hidden">
        <SectionHeader
          title="Confirmed Objectives"
          icon={<Target className="w-4 h-4" />}
          iconColor="text-indigo-600"
          badge={<Badge variant="outline" className="text-xs text-indigo-600 border-indigo-200">{workspace.objectives.length}</Badge>}
          sectionKey="objectives"
        />
        {expandedSections.objectives && (
          <CardContent className="pt-0 pb-4 px-4">
            {workspace.objectives.length === 0 ? (
              <p className="text-xs text-gray-500 italic">No objectives defined.</p>
            ) : (
              <div className="space-y-2">
                {workspace.objectives.map(obj => (
                  <div key={obj.id} className="flex items-start gap-3 p-2.5 border border-gray-100 rounded-md bg-gray-50/50">
                    <Target className="w-4 h-4 text-indigo-500 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] text-gray-900 font-medium leading-snug" style={{ fontWeight: 500 }}>
                        {obj.description}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className={`text-[10px] ${priorityBadge[obj.priority] || priorityBadge.medium}`}>
                          {obj.priority}
                        </Badge>
                        <span className="text-[10px] text-gray-400 flex items-center gap-1">
                          <User className="w-2.5 h-2.5" /> {obj.owner}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* ━━━ Delivery Items ━━━ */}
      <Card className="border border-gray-200 overflow-hidden">
        <SectionHeader
          title="Delivery Items"
          icon={<CheckCircle className="w-4 h-4" />}
          iconColor="text-emerald-600"
          badge={
            <Badge variant="outline" className="text-xs text-emerald-600 border-emerald-200">
              {doneItems}/{totalItems} done
            </Badge>
          }
          sectionKey="delivery"
        />
        {expandedSections.delivery && (
          <CardContent className="pt-0 pb-4 px-4">
            {/* Filters */}
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="h-7 text-xs w-[140px]">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  {categories.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="h-7 text-xs w-[140px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="needs_analysis">Needs analysis</SelectItem>
                  <SelectItem value="defined">Defined</SelectItem>
                  <SelectItem value="in_progress">In progress</SelectItem>
                  <SelectItem value="done">Done</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-[11px] text-gray-400 ml-auto">
                {filteredItems.length} of {totalItems} items
              </span>
            </div>

            {filteredItems.length === 0 ? (
              <p className="text-xs text-gray-500 italic py-4 text-center">No items match filters.</p>
            ) : (
              <div className="space-y-1.5">
                {filteredItems.map(item => (
                  <DeliveryItemRow
                    key={item.id}
                    item={item}
                    updating={updatingItem === item.id}
                    onStatusChange={(status) => updateItem(item.id, { status })}
                    onOwnerChange={(ownerName) => updateItem(item.id, { ownerName, owner: currentUser.id })}
                    currentUserName={currentUser.name || currentUser.email || ''}
                  />
                ))}
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* ━━━ Follow-ups ━━━ */}
      {workspace.followUps.length > 0 && (
        <Card className="border border-gray-200 overflow-hidden">
          <SectionHeader
            title="Follow-ups"
            icon={<Flag className="w-4 h-4" />}
            iconColor="text-amber-600"
            badge={
              openFollowUps > 0 ? (
                <Badge variant="outline" className="text-xs text-amber-600 border-amber-200">{openFollowUps} open</Badge>
              ) : (
                <Badge variant="outline" className="text-xs text-emerald-600 border-emerald-200">All resolved</Badge>
              )
            }
            sectionKey="followups"
          />
          {expandedSections.followups && (
            <CardContent className="pt-0 pb-4 px-4">
              <p className="text-xs text-gray-500 mb-3">
                Items flagged during insight review that need follow-up or assignment.
              </p>
              <div className="space-y-1.5">
                {workspace.followUps.map(fu => (
                  <div
                    key={fu.id}
                    className={`flex items-start gap-3 p-2.5 border rounded-md transition-opacity ${
                      fu.status === 'resolved' ? 'opacity-50 border-gray-100 bg-gray-50/30' : 'border-amber-100 bg-amber-50/30'
                    }`}
                  >
                    <Flag className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${fu.status === 'resolved' ? 'text-gray-400' : 'text-amber-500'}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] text-gray-900 leading-snug" style={{ fontWeight: 500 }}>
                        {fu.title}
                      </p>
                      {fu.action && (
                        <p className="text-[11px] text-gray-500 mt-0.5">{fu.action}</p>
                      )}
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className="text-[10px] text-gray-400">{fu.source}</span>
                        {fu.assignedToName && (
                          <span className="text-[10px] text-gray-500 flex items-center gap-0.5">
                            <User className="w-2.5 h-2.5" /> {fu.assignedToName}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {fu.status === 'open' && (
                        <>
                          <Button
                            variant="ghost" size="sm"
                            className="h-6 text-[10px] px-2"
                            disabled={updatingItem === fu.id}
                            onClick={() => updateItem(fu.id, {
                              assignedTo: currentUser.id,
                              assignedToName: currentUser.name || currentUser.email,
                            })}
                          >
                            <User className="w-3 h-3 mr-1" /> Assign me
                          </Button>
                          <Button
                            variant="ghost" size="sm"
                            className="h-6 text-[10px] px-2 text-emerald-600 hover:text-emerald-700"
                            disabled={updatingItem === fu.id}
                            onClick={() => updateItem(fu.id, { status: 'resolved' })}
                          >
                            <CheckCircle className="w-3 h-3 mr-1" /> Resolve
                          </Button>
                        </>
                      )}
                      {fu.status === 'resolved' && (
                        <Button
                          variant="ghost" size="sm"
                          className="h-6 text-[10px] px-2"
                          disabled={updatingItem === fu.id}
                          onClick={() => updateItem(fu.id, { status: 'open' })}
                        >
                          Reopen
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          )}
        </Card>
      )}
    </div>
  );
}

// ─── DeliveryItemRow ───

function DeliveryItemRow({
  item, updating, onStatusChange, onOwnerChange, currentUserName,
}: {
  item: DeliveryItem;
  updating: boolean;
  onStatusChange: (status: DeliveryStatus) => void;
  onOwnerChange: (name: string) => void;
  currentUserName: string;
}) {
  const [showSource, setShowSource] = useState(false);
  const sc = statusConfig[item.status];

  return (
    <div className="border border-gray-150 rounded-md p-2.5 hover:border-gray-300 transition-colors bg-white">
      <div className="flex items-start gap-2.5">
        {/* Category icon */}
        <span className={`mt-0.5 shrink-0 ${categoryColor[item.category] || 'text-gray-400'}`}>
          {categoryIcon[item.category] || categoryIcon.General}
        </span>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className="text-[13px] text-gray-900 leading-snug" style={{ fontWeight: 500 }}>
            {item.title}
          </p>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className="text-[10px] text-gray-400">{item.category}</span>
            {item.ownerName && (
              <span className="text-[10px] text-gray-500 flex items-center gap-0.5">
                <User className="w-2.5 h-2.5" /> {item.ownerName}
              </span>
            )}
            <button
              onClick={() => setShowSource(!showSource)}
              className="text-[10px] text-blue-500 hover:text-blue-700"
            >
              {showSource ? 'Hide source' : 'Source'}
            </button>
          </div>
          {showSource && (
            <p className="text-[10px] text-gray-400 mt-1 italic">{item.source}</p>
          )}
        </div>

        {/* Status + actions */}
        <div className="flex items-center gap-1.5 shrink-0">
          {!item.ownerName && (
            <Button
              variant="ghost" size="sm"
              className="h-6 text-[10px] px-2"
              disabled={updating}
              onClick={() => onOwnerChange(currentUserName)}
            >
              <User className="w-3 h-3 mr-0.5" /> Assign me
            </Button>
          )}
          <Select
            value={item.status}
            onValueChange={(v) => onStatusChange(v as DeliveryStatus)}
            disabled={updating}
          >
            <SelectTrigger className={`h-6 text-[10px] px-2 w-auto min-w-[110px] border rounded-full ${sc.color}`}>
              <span className="flex items-center gap-1">
                {updating ? <Loader2 className="w-3 h-3 animate-spin" /> : sc.icon}
                {sc.label}
              </span>
            </SelectTrigger>
            <SelectContent>
              {(Object.entries(statusConfig) as [DeliveryStatus, typeof sc][]).map(([key, cfg]) => (
                <SelectItem key={key} value={key}>
                  <span className="flex items-center gap-1.5 text-xs">
                    {cfg.icon} {cfg.label}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}
